const FirefoxHotkeyInstructions = require('../../web/markdown_display/FirefoxHotkeyInstructions');

/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictProvider = require('pict-provider');
const libMarked = require('marked');
// The shared renderer for markdown a user typed, deep-required from the dynamic inputs bundle the
// same way LIMS already requires _VocabularySelectorMap.js. Shared so a remark reads the same way
// on a panel, in the LIMS sample record and on a dynamic form.
const libUserMarkdown = require('headlight-dynamic-inputs-bundle/source/providers/_UserMarkdown.js');

class MarkdownProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
		this.markdownFilesMap =
		{
			FirefoxHotkeyInstructions: FirefoxHotkeyInstructions,
		};

		this._remarksCSSRegistered = false;

		// `{~MarkdownRemarks:Record.Notes~}` renders a remark as markdown.
		// `{~MarkdownRemarksClamped:Record.Notes~}` does the same but collapses a long one behind a
		// Read more toggle. Both take an optional template hash to render when the remark is empty:
		// `{~MarkdownRemarks:Record.Notes^SampleDetails-DatumValue-NA^~}`.
		this.pict.MetaTemplate.addPattern('{~MarkdownRemarks:', '~}', (pHash, pData, pContext) =>
		{
			return this.renderRemarksExpression(pHash, pData, pContext, false);
		});
		this.pict.MetaTemplate.addPattern('{~MarkdownRemarksClamped:', '~}', (pHash, pData, pContext) =>
		{
			return this.renderRemarksExpression(pHash, pData, pContext, true);
		});
	}

	/**
	 * Uses the name of the file to get the markdown from a file as a string (see the markdown_display folder).
	 * This will return null if the file name is not present.
	 * @param {String} name
	 * @return {String|null}
	 */
	getMarkdownByName(name)
	{
		return this.markdownFilesMap[name]?.markdown || null;
	}

	/**
	 * Uses the name of the file to get the title from a file as a string (see the markdown_display folder).
	 * This will return null if the file name is not present.
	 * @param {String} name
	 * @return {String|null}
	 */
	getMarkdownTitleByName(name)
	{
		return this.markdownFilesMap[name]?.title || null;
	}

	/**
	 * Uses the name of the file to get the markdown from a file converted to html.
	 * This will return null if the file name is not present.
	 * @param {String} name
	 * @return {String|null}
	 */
	getMarkdownAsHTMLByName(name)
	{
		const md = this.getMarkdownByName(name);
		if (!md)
		{
			return null;
		}
		return this.getMarkdownAsHTML(md);
	}

	/**
	 * Converts markdown to html.
	 * @param {String} markdown
	 * @return {String}
	 */
	getMarkdownAsHTML(markdown)
	{
		return this._addBulmaFlavor(String(libMarked.parse(markdown)));
	}

	/**
	 * Opens markdown in modal by file name (if file with that name is registered).
	 * @param {String} name
	 */
	openMarkdownInModalByName(name)
	{
		const md = this.getMarkdownByName(name);
		const title = this.getMarkdownTitleByName(name);
		if (md)
		{
			this.openMarkdownInModal(md, title);
		}
	}

	/**
	 * Opens markdown in a modal. Can optionally include a title for the modal.
	 * @param {String} markdown
	 * @param {String} [title]
	 */
	openMarkdownInModal(markdown, title)
	{
		const markdownModalOptions = {
			title: `<b>${ title || '' }</b>`,
			modalBodyTemplate: /*html*/`
				<div>
					${ this.getMarkdownAsHTML(markdown) }
				</div>
			`,
			modalFooterTemplate: /*html*/`
				<div class="columns">
					<div id="Footer-ButtonGroup" class="buttons is-right">
						<button id="Footer-Button-Cancel" type="button" class="button is-link is-soft" onclick="_Pict.fable.HeadlightModal.cancelCallback()">
							<span class="icon"><i class="fas fa-fw fa-times"></i></span>
							<span> Close </span>
						</button>
					</div>
				</div>
			`,
			confirmText: '',
			style: 'wide',
			cancelText: 'Cancel',
		};
		this.pict.services.HeadlightModal.spawnModal(markdownModalOptions);
	}

	/**
	 * Escapes a value for use as HTML text or as an attribute value.
	 *
	 * @param {any} pValue
	 * @return {String}
	 */
	escapeHTML(pValue)
	{
		return libUserMarkdown.escapeHTML(pValue);
	}

	/**
	 * Converts markdown a user typed (a remark, a note, a comment) to display HTML.
	 * Returns an empty string for an empty or null value.
	 *
	 * @param {any} pMarkdown
	 * @return {String}
	 */
	getUserMarkdownAsHTML(pMarkdown)
	{
		return libUserMarkdown.renderUserMarkdown(pMarkdown, this.log);
	}

	/**
	 * Renders a remark as the shared remarks block: the same markdown treatment the Sample
	 * Authorization form gives test remarks, so a remark reads the same way wherever it is shown.
	 *
	 * @param {any} pMarkdown - the remark source.
	 * @param {Object} [pOptions] - `Clamp` clamps the remark to three lines, with a Read more toggle when it runs longer.
	 * @return {String} - the rendered block, or an empty string when there is no remark.
	 */
	getRemarksAsHTML(pMarkdown, pOptions)
	{
		const tmpContent = this.getUserMarkdownAsHTML(pMarkdown);
		if (!tmpContent)
		{
			return '';
		}
		this.registerRemarksCSS();
		if (pOptions && pOptions.Clamp)
		{
			return `<details class="hl-md-remarks-collapse"><summary><div class="content hl-md-remarks hl-md-remarks-text">${ tmpContent }</div></summary></details>`;
		}
		return `<div class="content hl-md-remarks">${ tmpContent }</div>`;
	}

	/**
	 * Renders a `{~MarkdownRemarks:~}` / `{~MarkdownRemarksClamped:~}` expression.
	 *
	 * The expression is `<DataAddress>` or `<DataAddress>^<EmptyTemplateHash>^`. The empty-template
	 * hash is rendered in place of the remark when there is nothing to show -- it carries whatever
	 * "N/A" treatment the screen already uses.
	 *
	 * @param {String} pHash - the contents of the template expression.
	 * @param {any} pData - the Record the expression is rendering against.
	 * @param {Array<any>} pContext - the template context array.
	 * @param {Boolean} pClamp - true to collapse a long remark behind a Read more toggle.
	 * @return {String}
	 */
	renderRemarksExpression(pHash, pData, pContext, pClamp)
	{
		const tmpHashParts = String(pHash).split('^');
		const tmpRemarks = this.pict.resolveStateFromAddress(tmpHashParts[0].trim(), pData, pContext);
		const tmpContent = this.getRemarksAsHTML(tmpRemarks, { Clamp: pClamp });
		if (tmpContent)
		{
			return tmpContent;
		}
		const tmpEmptyTemplateHash = (tmpHashParts.length > 1) ? tmpHashParts[1].trim() : '';
		return tmpEmptyTemplateHash ? this.pict.parseTemplateByHash(tmpEmptyTemplateHash, pData, null, pContext) : '';
	}

	/**
	 * Puts the remarks styling in the page. Called the first time a remark actually renders rather
	 * than from the constructor, because the provider is built before the document is ready.
	 */
	registerRemarksCSS()
	{
		if (this._remarksCSSRegistered || !this.pict.CSSMap)
		{
			return;
		}
		this._remarksCSSRegistered = true;
		this.pict.CSSMap.addCSS('Headlight-Markdown-Remarks', libUserMarkdown.RemarksCSS, 500, 'MarkdownProvider');
		this.pict.CSSMap.injectCSS();
		libUserMarkdown.watchRemarksTruncation();
	}

	/**
	 * Adds Bulma flavor to the provided content (expected "default Bulma classes" on HTML elements).
	 * @param {string} pContent - The content to which to add Bulma flavor.
	 * @return {string} - The content with Bulma flavor added.
	 */
	_addBulmaFlavor(pContent)
	{
		if (pContent)
		{
			// for tables, add the table class
			pContent = pContent.replace(/<table>/g, '<table class="table is-fullwidth is-striped is-hoverable">');
			// for images, add the image class
			pContent = pContent.replace(/<img/g, '<img class="image"');
			// h1-h6
			pContent = pContent.replace(/<h1>/g, '<h1 class="title is-1">');
			pContent = pContent.replace(/<h2>/g, '<h2 class="title is-2">');
			pContent = pContent.replace(/<h3>/g, '<h3 class="title is-3">');
			pContent = pContent.replace(/<h4>/g, '<h4 class="title is-4">');
			pContent = pContent.replace(/<h5>/g, '<h5 class="title is-5">');
			pContent = pContent.replace(/<h6>/g, '<h6 class="title is-6">');
		}
		return pContent;
	}
}

module.exports = MarkdownProvider;
module.exports.default_configuration = {};
